import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { OriginalsNav } from '@/components/OriginalsNav';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { ShoppingBag, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OriginalsHome() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [bannerMessages, setBannerMessages] = useState([]);
  const [heroBgImage, setHeroBgImage] = useState('https://images.unsplash.com/photo-1761230684438-34206743f4c7?w=1600');
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
    fetchBannerMessages();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      if (response.data.originals_home_bg) {
        setHeroBgImage(response.data.originals_home_bg);
      }
    } catch (error) {
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchBannerMessages = async () => {
    try {
      const response = await axios.get(`${API}/banner-messages`);
      setBannerMessages(response.data.messages || []);
    } catch (error) {
    }
  };

  // Use only the messages from the API (no hardcoded defaults)
  const allBannerItems = bannerMessages.map(m => ({ text: m.text, isDefault: false }));

  const categories = ['all', ...new Set(products.map(p => p.category))];
  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  const formatCategory = (cat) => {
    return cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Create duplicated content for seamless infinite scroll - repeat enough times to fill screen
  const bannerContent = allBannerItems.length > 0 ? allBannerItems : [];
  
  // Repeat content multiple times to ensure it fills the full width
  const repeatedContent = [];
  for (let i = 0; i < 10; i++) {
    bannerContent.forEach((item, j) => {
      repeatedContent.push({ ...item, key: `${i}-${j}` });
    });
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="originals-home">
      <OriginalsNav />
      
      {/* Dynamic Banner - positioned below fixed nav */}
      {bannerContent.length > 0 && (
        <div className="fixed top-20 left-0 right-0 z-40 bg-[#d4af37] py-2 overflow-hidden">
          <div className="marquee-track">
            {repeatedContent.map((item) => (
              <span key={item.key} className="marquee-item font-cinzel text-sm tracking-widest text-black">
                <span className="text-lg">✦</span> {item.text} <span className="text-lg">✦</span>
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Hero Section - extra top padding to account for banner */}
      <section className={`relative ${bannerContent.length > 0 ? 'pt-32' : 'pt-20'}`}>
        <div 
          className="h-[60vh] min-h-[400px] bg-cover bg-center relative"
          style={{ backgroundImage: `url(${heroBgImage})` }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 vignette" />
          <div className="absolute inset-0 grain-overlay" />
          
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-7xl text-white mb-4 opacity-0 animate-slide-up" style={{ animationFillMode: 'forwards' }}>
              Ben Jefferies Originals
            </h1>
            <p className="font-cormorant text-xl lg:text-2xl text-[#a1a1aa] max-w-2xl opacity-0 animate-slide-up animation-delay-200" style={{ animationFillMode: 'forwards' }}>
              Unique metal sculptures and art pieces — where industrial precision meets artistic vision
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-4 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-full font-cinzel text-sm tracking-wider whitespace-nowrap transition-colors ${
                  activeCategory === cat
                    ? 'bg-[#d4af37] text-black'
                    : 'border border-[#27272a] text-[#a1a1aa] hover:border-[#d4af37] hover:text-[#d4af37]'
                }`}
                data-testid={`category-${cat}`}
              >
                {formatCategory(cat)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-[#171717] h-80 rounded-lg mb-4" />
                  <div className="bg-[#171717] h-4 w-3/4 rounded mb-2" />
                  <div className="bg-[#171717] h-4 w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-cormorant text-xl text-[#a1a1aa]">No products found in this category</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {filteredProducts.map((product, index) => (
                <Link 
                  key={product.id}
                  to={`/originals/product/${product.id}`}
                  className="group card-originals opacity-0 animate-slide-up"
                  style={{ animationFillMode: 'forwards', animationDelay: `${index * 100}ms` }}
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Image Container */}
                  <div className="relative overflow-hidden rounded-lg mb-6 bg-[#171717]">
                    <div className="aspect-[4/5]">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    
                    {/* Overlay on hover */}
                    <div className="card-overlay absolute inset-0 bg-black/40 opacity-0 transition-opacity flex items-center justify-center">
                      <button
                        onClick={(e) => handleAddToCart(e, product)}
                        className="px-6 py-3 bg-white text-black font-cinzel text-sm tracking-wider flex items-center gap-2 hover:bg-[#d4af37] transition-colors"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        Add to Cart
                      </button>
                    </div>
                  </div>

                  {/* Product Info - Museum Plaque Style */}
                  <div className="space-y-2">
                    <span className="font-cinzel text-xs text-[#d4af37] tracking-widest uppercase">
                      {formatCategory(product.category)}
                    </span>
                    <h3 className="font-playfair text-xl text-white group-hover:text-[#d4af37] transition-colors">
                      {product.name}
                    </h3>
                    <p className="font-cormorant text-[#a1a1aa] line-clamp-2">
                      {product.description}
                    </p>
                    <p className="font-mono text-lg text-[#d4af37]">
                      £{Math.floor(product.price)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quote Request CTA */}
      <section className="py-20 border-t border-[#27272a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-playfair text-3xl lg:text-4xl text-white mb-4">
            Looking for Something Custom?
          </h2>
          <p className="font-cormorant text-lg text-[#a1a1aa] mb-8 max-w-2xl mx-auto">
            Commission a unique piece tailored to your space. From concept to creation, 
            we bring your artistic vision to life in metal.
          </p>
          <Link 
            to="/engineering/quote"
            className="btn-originals inline-flex items-center gap-3 px-8 py-4 rounded-full font-cinzel tracking-wider"
            data-testid="custom-quote-btn"
          >
            Request Custom Quote
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-cormorant text-sm text-[#a1a1aa]">
              © 2024 Ben Jefferies Originals. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/originals/about" className="font-cormorant text-sm text-[#a1a1aa] hover:text-[#d4af37]">About</Link>
              <Link to="/engineering" className="font-cormorant text-sm text-[#a1a1aa] hover:text-[#d4af37]">BJ Engineering</Link>
              <Link to="/engineering/contact" className="font-cormorant text-sm text-[#a1a1aa] hover:text-[#d4af37]">Contact</Link>
              <Link to="/admin" className="font-cormorant text-sm text-[#52525b] hover:text-[#a1a1aa]">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
